import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { StatusDot } from '@/components/ui/StatusDot'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { Modal } from '@/components/ui/Modal'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useAuthority } from '@/hooks/useAuthority'
import { TestsService, type Test } from '@/services/modules/TestsService'
import { toast } from '@/components/ui/Toast'

export function WorkflowTestsPage() {
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar exame' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(TestsService, { size: 15, reloadKey: reload , filters: sf.filters})

  const exp = useDataTableExport({
    filename: 'tests',
    service: TestsService as never,
    filters: sf.filters,
    columns: [], // TODO: configurar columns para export
  })
  const [openForm, setOpenForm] = useState<{ open: boolean; item?: Test }>({ open: false })
  const [openDelete, setOpenDelete] = useState<Test | null>(null)
  const { can } = useAuthority()
  const canCreate = can('workflow.tests.create')
  const canEdit = can('workflow.tests.edit')
  const canDelete = can('workflow.tests.delete')

  async function handleSave(payload: Partial<Test>) {
    try {
      if (openForm.item) {
        await TestsService.update(openForm.item.id, payload); toast.success('Exame atualizado.')
      } else {
        await TestsService.create(payload); toast.success('Exame criado.')
      }
      setOpenForm({ open: false }); setReload(v => v + 1)
    } catch (e) { toast.error((e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.') }
  }
  async function handleDelete() {
    if (!openDelete) return
    try { await TestsService.destroy(openDelete.id); toast.success('Exame removido.'); setOpenDelete(null); setReload(v => v + 1) }
    catch { toast.error('Erro ao remover.') }
  }

  const columns: DataTableColumn<Test>[] = [
    { key: 'test',   header: 'Exame', render: t => <span className="font-medium text-gray-800">{t.test}</span>, sortable: true, alwaysVisible: true },
    { key: 'code',   header: 'TUSS',  align: 'center', render: t => <span className="text-xs text-gray-500">{t.code ?? '—'}</span>, className: 'w-40' },
    { key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: t => (
        <StatusDot value={t.status} />
      ),
    },
  ]

  return (
    <>
      <PageHeader title="Exames" subtitle="Catálogo de exames (TUSS)"
        crumbs={[{ label: 'Configurações', to: '/settings' }, { label: 'Workflow' }, { label: 'Exames' }]}
        actions={canCreate ? <Button onClick={() => setOpenForm({ open: true })} iconLeft={<Plus size={14} />}>Novo exame</Button> : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId="workflow.tests"
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        rowActions={(t) => [
          ...(canEdit ? [{ key: 'edit',   label: 'Editar',  icon: <Pencil size={14} />,  onClick: () => setOpenForm({ open: true, item: t }) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />,  danger: true, divider: canEdit, onClick: () => setOpenDelete(t) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <TestForm open={openForm.open} item={openForm.item} onClose={() => setOpenForm({ open: false })} onSave={handleSave} />
      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover exame?"
        description={`Tem certeza que deseja remover "${openDelete?.test}"?`}
        confirmLabel="Remover"
        variant="danger"
      />  
    {sf.drawer}
    </>
  )
}

function TestForm({ open, item, onClose, onSave }: { open: boolean; item?: Test; onClose: () => void; onSave: (payload: Partial<Test>) => Promise<void> }) {
  const [test, setTest] = useState('')
  const [code, setCode] = useState('')
  const [status, setStatus] = useState<'ATIVO' | 'INATIVO'>('ATIVO')
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    if (open) { setTest(item?.test ?? ''); setCode(item?.code ?? ''); setStatus(item?.status ?? 'ATIVO') }
  }, [open, item])

  async function submit() {
    setSaving(true)
    await onSave({ test, code: code || null, status })
    setSaving(false)
  }
  return (
    <Modal open={open} onClose={onClose}
      title={item ? 'Editar exame' : 'Novo exame'}
      footer={<><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={submit} loading={saving} disabled={!test.trim()}>Salvar</Button></>}
    >
      <div className="space-y-3">
        <Input label="Exame" required value={test} onChange={e => setTest(e.target.value)} placeholder="Hemograma Completo" />
        <Input label="Código TUSS" value={code} onChange={e => setCode(e.target.value)} placeholder="4.03.04.01-9" />
        <StatusToggle value={status} onChange={setStatus} />
      </div>
    </Modal>
  )
}
